import { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { Route, Routes, BrowserRouter as Router, useLocation } from "react-router-dom";
import { StyledEngineProvider, ThemeProvider, createTheme } from "@mui/material/styles";
import KentroGuard from "./components/KentroGuard.js";
import * as Sentry from "@sentry/browser";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ErrorBoundary } from "react-error-boundary";
import { CssBaseline } from "@mui/material";

import "./index.scss";
import colors from "./_colors.scss";
import "react-table-6/react-table.css";
import Header from "./components/Header.js";
import Footer from "./components/Footer.js";
import Protected from "./components/Protected.js";
import GuestOnly from "./components/GuestOnly.js";
import AdminOnly from "./components/AdminOnly.js";
import ErrorFallback from "./components/ErrorFallback.js";
import Snackbar from "./components/Snackbar.js";
import NotFound from "./screens/NotFound.js";
import SignIn from "./screens/SignIn.js";
import ForgotPassword from "./screens/ForgotPassword.js";
import ResetPassword from "./screens/ResetPassword.js";
import SignUp from "./screens/SignUp.js";
import InvitedSignUp from "./screens/InvitedSignUp.js";
import Auth from "./screens/Auth.js";
import Home from "./screens/Home.js";
import InviteUser from "./screens/InviteUser.js";
import InvitedUsers from "./screens/InvitedUsers.js";
import Users from "./screens/Users.js";
import AdminActions from "./screens/AdminActions.js";
import { adjustColors, jwt, colorSuggestions } from "./utils/index.js";
// Owners
import SelectOwner from "./screens/owners/Select.js";
import CreateOwner from "./screens/owners/Create.js";
import OwnersList from "./screens/owners/List.js";
import OwnersPdf from "./screens/owners/Pdf.js";
import ChangeCode from "./screens/owners/ChangeCode.js";
import ArchiveOwners from "./screens/owners/ArchiveOwners.js";
import OwnerDisplayCodes from "./screens/owners/OwnerDisplayCodes.js";
// Inspectors
import CreateInspector from "./screens/inspectors/Create.js";
import SelectInspector from "./screens/inspectors/Select.js";
import InspectorsList from "./screens/inspectors/List.js";
import InspectorsPdf from "./screens/inspectors/Pdf.js";
// Animals
import CreateAnimal from "./screens/animals/Create.js";
import SelectAnimal from "./screens/animals/Select.js";
import AnimalsList from "./screens/animals/List.js";
import AnimalsPdf from "./screens/animals/Pdf.js";
import UdderCreate from "./screens/animals/UdderCreate.js";
import UdderEdit from "./screens/animals/UdderEdit.js";
import Supervision from "./screens/animals/Supervision.js";
import ReproductiveHistory from "./screens/animals/ReproductiveHistory.js";
import CurrentOwner from "./screens/animals/CurrentOwner.js";
import ChangeRegistrationCode from "./screens/animals/ChangeRegistrationCode.js";
import GroupCulling from "./screens/animals/GroupCulling.js";
// Lambings
import CreateLambing from "./screens/lambings/Create.js";
import SelectLambing from "./screens/lambings/Select.js";
import WeaningDate from "./screens/lambings/WeaningDate.js";
import LambWeight from "./screens/lambings/LambWeight.js";
import ManageLambings from "./screens/lambings/Manage.js";
import DeleteLambing from "./screens/lambings/Delete.js";
import LambCodeHistory from "./screens/lambings/LambCodeHistory.js";
// Matings
import CreateMating from "./screens/matings/Create.js";
import MatingsList from "./screens/matings/List.js";
import MatingsRelationship from "./screens/matings/MatingsRelationship.js";
import DeleteMating from "./screens/matings/Delete.js";
// Milk recordings
import CreateMilkRecording from "./screens/milkRecordings/Create.js";
import SelectMilkRecording from "./screens/milkRecordings/Select.js";
import CreateAnimalMilkRecording from "./screens/milkRecordings/AnimalCreate.js";
import SelectAnimalMilkRecording from "./screens/milkRecordings/AnimalSelect.js";
import AnimalMilkRecordingMetrics from "./screens/milkRecordings/Metrics.js";
import TotalMilkCalculation from "./screens/milkRecordings/Calculate.js";
import EndMilkingPeriod from "./screens/milkRecordings/EndMilkingPeriod.js";
import UpdateEndMilkingPeriod from "./screens/milkRecordings/UpdateEndMilkingPeriod.js";
// Orders
import CreateOrder from "./screens/orders/Create.js"
import CreateAnimalsToSale from "./screens/orders/AnimalsToSaleCreate.js"
import AnimalsToSale from "./screens/orders/AnimalsToSale.js";
import OrdersHistory from "./screens/orders/SalesHistory.js";
import EditAvailableAnimals from "./screens/orders/AnimalsToSaleEdit.js";
import AnimalTransfer from "./screens/orders/AnimalTransfer.js";
import OwnerTransfer from "./screens/orders/OwnerTransfer.js";
// Info
import Pedigree from "./screens/tools/Pedigree.js";
import MaleFamilyTree from "./screens/tools/MaleFamilyTree.js";
import FamilyRelatives from "./screens/tools/FamilyRelatives.js";
import MonthlyMilkProduction from "./screens/tools/MonthlyMilkProduction.js";
import AnimalRegistration from "./screens/tools/AnimalRegistration.js";
import RegistrationLogs from "./screens/tools/RegistrationLogs.js";
import OwnerPopulation from "./screens/tools/OwnerPopulation.js";
import OwnerPopulationDetailed from "./screens/tools/OwnerPopulationDetailed.js";
import YearMilking from "./screens/tools/YearMilking.js";
import ProblemsReport from "./screens/tools/ProblemsReport.js";
// Scrapie
import CreateScrapie from "./screens/scrapie/Create.js";
import UpdateScrapie from "./screens/scrapie/Update.js";
import DeleteScrapie from "./screens/scrapie/Delete.js";
import ViewScrapie from "./screens/scrapie/View.js";
import CreateLambScrapie from "./screens/scrapie/LambCreate.js";
import UpdateLambScrapie from "./screens/scrapie/LambUpdate.js";
import DeleteLambScrapie from "./screens/scrapie/LambDelete.js";
import ViewLambScrapie from "./screens/scrapie/LambView.js";
// Organization
import Organization from "./screens/Organization.js";

import { Auth0Provider } from "@auth0/auth0-react";

import "./i18n.js";

const {
	NODE_ENV,
	REACT_APP_SENTRY_DSN,
	REACT_APP_SENTRY_ENVIRONMENT,
	REACT_APP_AUTH0_CLIENT_ID,
	REACT_APP_AUTH0_DOMAIN,
} = process.env;

Sentry.init({
	dsn: REACT_APP_SENTRY_DSN,
	environment: REACT_APP_SENTRY_ENVIRONMENT,
	ignoreErrors: [
		"ResizeObserver loop limit exceeded",
		"Non-Error promise rejection captured",
	],
	enabled: NODE_ENV === "production",
});

const theme = createTheme({
	palette: {
		primary: { main: colors.primary },
		secondary: { main: colors.secondary || colorSuggestions.secondary },
		third: { main: colors.third || colorSuggestions.third },

		primaryLight: { main: adjustColors(colors.primary, 100) },
		primaryDark: { main: adjustColors(colors.primary, -80) },
		secondaryLight: { main: adjustColors(colors.secondary || colorSuggestions.secondary, 100) },
		secondaryDark: { main: adjustColors(colors.secondary || colorSuggestions.secondary, -80) },
		thirdLight: { main: adjustColors(colors.third || colorSuggestions.third, 100) },
		thirdDark: { main: adjustColors(colors.third || colorSuggestions.third, -80) },

		success: { main: colors.success },
		error: { main: colors.error },
		warning: { main: colors.warning },
		info: { main: colors.info },

		dark: { main: colors.dark },
		light: { main: colors.light },
		grey: { main: colors.grey },
		greyDark: { main: colors.greyDark },
		green: { main: colors.green },
		white: { main: "#ffffff" },
	},
});

const App = () => {
	const location = useLocation();
	const [authenticated, setAuthenticated] = useState(false);

	useEffect(() => {
		setAuthenticated(jwt.isAuthenticated());
	}, [location]);

	return (
		<StyledEngineProvider injectFirst>
			<CssBaseline />
			<ThemeProvider theme={theme}>
				<ErrorBoundary FallbackComponent={ErrorFallback}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<KentroGuard>
							{authenticated && <Header isAuthenticated={authenticated} />}
							<main style={{ position: "relative", zIndex: 0, height: `calc(100vh - ${authenticated ? "130" : "0"}px)` }}>
								<Routes>
									<Route index element={<GuestOnly c={<SignIn />} />} />
									<Route path="auth" element={<GuestOnly c={<Auth />} />} />
									<Route path="forgot-password" element={<GuestOnly c={<ForgotPassword />} />} />
									<Route path="reset-password" element={<GuestOnly c={<ResetPassword />} />} />
									<Route path="sign-up" element={<GuestOnly c={<SignUp />} />} />
									<Route path="complete-registration" element={<GuestOnly c={<InvitedSignUp />} />} />
									<Route path="home" element={<Protected c={<Home />} />} />
									<Route path="create-invited-user" element={<AdminOnly c={<InviteUser />} />} />
									<Route path="invited-users" element={<AdminOnly c={<InvitedUsers />} />} />
									<Route path="admin-actions" element={<AdminOnly c={<AdminActions />} />} />
									{/* OWNERS */}
									<Route path="owners/create" element={<Protected c={<CreateOwner />} />} />
									<Route path="owners/select" element={<Protected c={<SelectOwner />} />} />
									<Route path="owners/preview" element={<Protected c={<OwnersList />} />} />
									<Route path="owners/change-code" element={<Protected c={<ChangeCode />} />} />
									<Route path="owners/archive" element={<Protected c={<ArchiveOwners />} />} />
								<Route path="owners/display-codes" element={<AdminOnly c={<OwnerDisplayCodes />} />} />
									<Route path="print/owners" element={<Protected c={<OwnersPdf />} />} />
									{/* INSPECTORS */}
									<Route path="inspectors/create" element={<Protected c={<CreateInspector />} />} />
									<Route path="inspectors/select" element={<Protected c={<SelectInspector />} />} />
									<Route path="inspectors/preview" element={<Protected c={<InspectorsList />} />} />
									<Route path="print/inspectors" element={<Protected c={<InspectorsPdf />} />} />
									{/* ANIMALS */}
									<Route path="animals/create" element={<Protected c={<CreateAnimal />} />} />
									<Route path="animals/select" element={<Protected c={<SelectAnimal />} />} />
									<Route path="animals/preview" element={<Protected c={<AnimalsList />} />} />
									<Route path="print/animals" element={<Protected c={<AnimalsPdf />} />} />
									<Route path="animals/udder/create" element={<Protected c={<UdderCreate />} />} />
									<Route path="animals/udder/select" element={<Protected c={<UdderEdit />} />} />
									<Route path="animals/supervision" element={<Protected c={<Supervision />} />} />
									<Route path="animals/reproductive-history" element={<Protected c={<ReproductiveHistory />} />} />
									<Route path="animals/current-owner" element={<Protected c={<CurrentOwner />} />} />
									<Route path="animals/registration-change" element={<Protected c={<ChangeRegistrationCode />} />} />
									<Route path="animals/group-culling" element={<Protected c={<GroupCulling />} />} />
									{/* LAMBINGS */}
									<Route path="lambings/create" element={<Protected c={<CreateLambing />} />} />
									<Route path="lambings/select" element={<Protected c={<SelectLambing />} />} />
									<Route path="lambings/lambs/end" element={<Protected c={<WeaningDate />} />} />
									<Route path="/lambings/lamb-weight" element={<Protected c={<LambWeight />} />} />
									<Route path="/lambings/manage" element={<Protected c={<ManageLambings />} />} />
									<Route path="/lambings/lamb-code-history" element={<Protected c={<LambCodeHistory />} />} />
									<Route path="/lambings/delete" element={<Protected c={<DeleteLambing />} />} />
									{/* MATINGS */}
									<Route path="matings/create" element={<Protected c={<CreateMating />} />} />
									<Route path="matings/preview" element={<Protected c={<MatingsList />} />} />
									<Route path="matings/relationships" element={<Protected c={<MatingsRelationship />} />} />
									<Route path="matings/delete" element={<Protected c={<DeleteMating />} />} />
									{/* INFO */}
									<Route path="info/pedigree" element={<Protected c={<Pedigree />} />} />
									<Route path="info/male/tree" element={<Protected c={<MaleFamilyTree />} />} />
									<Route path="info/animal/relatives" element={<Protected c={<FamilyRelatives />} />} />
									<Route path="info/monthly/milk-production" element={<Protected c={<MonthlyMilkProduction />} />} />
									<Route path="info/animal/registration" element={<Protected c={<AnimalRegistration />} />} />
									<Route path="tools/registration-logs" element={<Protected c={<RegistrationLogs />} />} />
									<Route path="info/owner/population/detailed" element={<Protected c={<OwnerPopulationDetailed />} />} />
									<Route path="info/owner/population" element={<Protected c={<OwnerPopulation />} />} />
									<Route path="info/year-milking" element={<Protected c={<YearMilking />} />} />
									<Route path="info/problems/report" element={<Protected c={<ProblemsReport />} />} />
									{/* MILK RECORDINGS */}
									<Route path="milk-recordings/create" element={<Protected c={<CreateMilkRecording />} />} />
									<Route path="milk-recordings/select" element={<Protected c={<SelectMilkRecording />} />} />
									<Route path="animals-milk-recordings/create" element={<Protected c={<CreateAnimalMilkRecording />} />} />
									<Route path="animals-milk-recordings/select" element={<Protected c={<SelectAnimalMilkRecording />} />} />
									<Route path="animals-milk-recordings/metrics" element={<Protected c={<AnimalMilkRecordingMetrics />} />} />
									<Route path="animals-milk-recordings/calculation" element={<Protected c={<TotalMilkCalculation />} />} />
									<Route path="animals-milk-recordings/end" element={<Protected c={<EndMilkingPeriod />} />} />
									<Route path="animals-milk-recordings/end/update" element={<Protected c={<UpdateEndMilkingPeriod />} />} />
									{/* ORDERS */}
									<Route path="sales/orders/create" element={<Protected c={<CreateOrder />} />} />
									<Route path="sales/animals/create" element={<Protected c={<CreateAnimalsToSale />} />} />
									<Route path="sales/animals" element={<Protected c={<AnimalsToSale />} />} />
									<Route path="sales/orders" element={<Protected c={<OrdersHistory />} />} />
									<Route path="sales/animals/select" element={<Protected c={<EditAvailableAnimals />} />} />
									<Route path="sales/animals/transfer" element={<Protected c={<AnimalTransfer />} />} />
									<Route path="sales/owners/transfer" element={<Protected c={<OwnerTransfer />} />} />
									{/* SCRAPIE */}
									<Route path="scrapie/add" element={<Protected c={<CreateScrapie />} />} />
									<Route path="scrapie/update" element={<Protected c={<UpdateScrapie />} />} />
									<Route path="scrapie/delete" element={<Protected c={<DeleteScrapie />} />} />
									<Route path="scrapie/view" element={<Protected c={<ViewScrapie />} />} />
									<Route path="scrapie/lamb-add" element={<Protected c={<CreateLambScrapie />} />} />
									<Route path="scrapie/lamb-update" element={<Protected c={<UpdateLambScrapie />} />} />
									<Route path="scrapie/lamb-delete" element={<Protected c={<DeleteLambScrapie />} />} />
									<Route path="scrapie/lamb-view" element={<Protected c={<ViewLambScrapie />} />} />
									{/* ORGANIZATION */}
									<Route path="organization" element={<Protected c={<Organization />} />} />

									<Route path="users" element={<Protected c={<Users />} />} />
									<Route path="*" element={<NotFound />} />
								</Routes>
							</main>
							{authenticated && <Footer />}
							<Snackbar />
						</KentroGuard>
					</LocalizationProvider>
				</ErrorBoundary>
			</ThemeProvider>
		</StyledEngineProvider>
	);
};

const root = ReactDOM.createRoot(document.querySelector("#root"));
root.render(
	<Auth0Provider
		domain={REACT_APP_AUTH0_DOMAIN}
		clientId={REACT_APP_AUTH0_CLIENT_ID}
		authorizationParams={{
			redirect_uri: window.location.origin
		}}
	>
		<Router>
			<App />
		</Router>
	</Auth0Provider>
);
